import React, { useEffect, useState, useCallback } from 'react';
import {
  HiOutlineArchiveBox,
  HiOutlineCurrencyDollar,
  HiOutlineExclamationTriangle,
  HiOutlineArrowTrendingUp,
  HiOutlineUsers,
  HiOutlineArrowsRightLeft,
  HiOutlineArrowPath,
} from 'react-icons/hi2';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import sparePartsService from '../services/sparePartsService';
import plantService from '../services/plantService';
import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import './InventoryDashboard.css';

const PIE_COLORS = ['#6366f1', '#06b6d4', SEMANTIC_SUCCESS_MID, '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="inv-dash-stat-card">
    <div className="inv-dash-stat-icon" style={{ background: color + '20', color }}>
      <Icon />
    </div>
    <div className="inv-dash-stat-body">
      <div className="inv-dash-stat-value">{value}</div>
      <div className="inv-dash-stat-label">{label}</div>
      {sub && <div className="inv-dash-stat-sub">{sub}</div>}
    </div>
  </div>
);

const InventoryDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [movementChart, setMovementChart] = useState([]);
  const [stockTrend, setStockTrend] = useState([]);
  const [topParts, setTopParts] = useState([]);
  const [vendorCost, setVendorCost] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [plants, setPlants] = useState([]);
  const [selectedPlantId, setSelectedPlantId] = useState("");

  const handlePlantChange = (val) => {
    setSelectedPlantId(val);

  };

  // Safely attempt a service call, returning null on failure
  const safe = async (fn) => { try { return await fn(); } catch { return null; } };

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // ── KPI summary: computed from parts (always works) + optional backend summary ──
      const [partsRes, summaryRes, plantsRes] = await Promise.all([
        safe(() => sparePartsService.getParts({ plantId: selectedPlantId })),
        safe(() => sparePartsService.getDashboardSummary(selectedPlantId)),
        safe(() => plantService.getAllPlants())
      ]);

      const pData = plantsRes || [];
      setPlants(pData);

      // Auto-select plant if only 1 exists
      if (pData?.length === 1 && !selectedPlantId) {
        setSelectedPlantId(pData[0].plantId.toString());
      }

      // No redundant auto-selection here as it's handled in useState initialization

      const parts = partsRes || [];

      // Compute KPIs locally from parts list as reliable source
      const totalSpareParts = parts.length;
      const lowStockCount = parts.filter(p => Number(p.currentStock) <= Number(p.minimumStock)).length;
      const overstockCount = parts.filter(p => {
        const maxS = Number(p.minimumStock) * 3;
        return maxS > 0 && Number(p.currentStock) > maxS;
      }).length;
      const totalVendors = new Set(parts.map(p => p.vendor).filter(Boolean)).size;

      // Use backend summary for data that needs transactions (value, movements)
      const backendSummary = summaryRes?.data;

      setSummary({
        totalSpareParts,
        lowStockCount,
        overstockCount,
        totalVendors,
        totalInventoryValue: backendSummary?.totalInventoryValue ?? 0,
        movementsToday: backendSummary?.movementsToday ?? 0,
        movementsThisMonth: backendSummary?.movementsThisMonth ?? 0,
      });

      // ── Chart data: load independently, failures just leave charts empty ──
      const [movementRes, trendRes, topRes, vendorRes] = await Promise.all([
        safe(() => sparePartsService.getMovementChart(selectedPlantId)),
        safe(() => sparePartsService.getStockTrend(selectedPlantId)),
        safe(() => sparePartsService.getTopParts(selectedPlantId)),
        safe(() => sparePartsService.getVendorCostDistribution(selectedPlantId)),
      ]);

      setMovementChart(movementRes?.data || []);
      setStockTrend(trendRes?.data || []);
      setTopParts(topRes?.data || []);

      // If vendorCost endpoint fails, compute it from parts directly
      if (vendorRes?.data?.length > 0) {
        setVendorCost(vendorRes.data);
      } else {
        // Fallback: group parts count by vendor
        const vendorMap = {};
        parts.forEach(p => {
          if (p.vendor) {
            vendorMap[p.vendor] = (vendorMap[p.vendor] || 0) + 1;
          }
        });
        setVendorCost(
          Object.entries(vendorMap).map(([vendor, count]) => ({ vendor, value: count }))
        );
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedPlantId]);

  useEffect(() => {
    loadAll();
  }, [loadAll, selectedPlantId]);


  const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 }));

  if (loading) {
    return (
      <div className="inv-dash-page">
        <div className="inv-dash-loading">
          <div className="inv-dash-spinner" />
          <p>Loading Inventory Dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pagecontainer inv-dash-page">
      {/* Header */}
      <div className="page-header">
        <div className='header-contain'>
          <h1>Inventory Dashboard</h1>
          <p>Real-time analytics for spare parts &amp; stock movements</p>
        </div>
        <div className='header-actions'>
          <select 
            className="rounded-lg mr-2 border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base py-2 px-3 bg-card"
            value={selectedPlantId}
            onChange={(e) => handlePlantChange(e.target.value)}
          >
            <option value="">All Plants</option>
            {plants.map(p => (
              <option key={p.plantId} value={p.plantId}>{p.plantName}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={loadAll} title="Refresh">
            <HiOutlineArrowPath /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="inv-dash-error">{error}</div>}

      {/* KPI Widget Row */}
      <div className="inv-dash-stats-grid">
        <StatCard
          icon={HiOutlineArchiveBox}
          label="Total Spare Parts"
          value={fmt(summary?.totalSpareParts)}
          color="#6366f1"
        />
        <StatCard
          icon={HiOutlineCurrencyDollar}
          label="Total Inventory Value"
          value={`₹${fmt(summary?.totalInventoryValue)}`}
          color={SEMANTIC_SUCCESS_MID}
        />
        <StatCard
          icon={HiOutlineExclamationTriangle}
          label="Low Stock Items"
          value={fmt(summary?.lowStockCount)}
          sub="Below minimum level"
          color="#ef4444"
        />
        <StatCard
          icon={HiOutlineArrowTrendingUp}
          label="Overstock Items"
          value={fmt(summary?.overstockCount)}
          sub="Above 3× minimum"
          color="#f59e0b"
        />
        <StatCard
          icon={HiOutlineUsers}
          label="Total Vendors"
          value={fmt(summary?.totalVendors)}
          color="#8b5cf6"
        />
        <StatCard
          icon={HiOutlineArrowsRightLeft}
          label="Movements Today"
          value={fmt(summary?.movementsToday)}
          sub={`${fmt(summary?.movementsThisMonth)} this month`}
          color="#06b6d4"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="inv-dash-charts-row">
        {/* Stock In vs Stock Out - Monthly */}
        <div className="inv-dash-chart-card wide">
          <h3 className="inv-dash-chart-title">📦 Stock In vs Stock Out (Monthly)</h3>
          {movementChart.length === 0 ? (
            <div className="inv-dash-empty">No movement data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={movementChart} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-secondary)" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
                <YAxis tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
                <Tooltip
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-primary)', borderRadius: 8 }}
                  labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                />
                <Legend />
                <Bar dataKey="stockIn" name="Stock In" fill="#6366f1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="stockOut" name="Stock Out" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Inventory Cost Distribution by Vendor */}
        <div className="inv-dash-chart-card">
          <h3 className="inv-dash-chart-title">🏭 Cost by Vendor</h3>
          {vendorCost.length === 0 ? (
            <div className="inv-dash-empty">No vendor cost data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={vendorCost}
                  dataKey="value"
                  nameKey="vendor"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ vendor, percent }) => `${vendor} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {vendorCost.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => [`₹${Number(v).toLocaleString()}`, 'Value']}
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-primary)', borderRadius: 8 }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="inv-dash-charts-row">
        {/* Stock Trend – last 30 days */}
        <div className="inv-dash-chart-card wide">
          <h3 className="inv-dash-chart-title">📈 Stock Trend (Last 30 Days)</h3>
          {stockTrend.length === 0 ? (
            <div className="inv-dash-empty">No trend data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={stockTrend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-secondary)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: 'var(--text-secondary)' }}
                  interval={4}
                  tickFormatter={(d) => d.slice(5)}
                />
                <YAxis tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
                <Tooltip
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-primary)', borderRadius: 8 }}
                  labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                />
                <Legend />
                <Line type="monotone" dataKey="stockIn" name="In" stroke={SEMANTIC_SUCCESS_MID} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="stockOut" name="Out" stroke="#ef4444" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="net" name="Net" stroke="#6366f1" strokeWidth={2} dot={false} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top Used Spare Parts */}
        <div className="inv-dash-chart-card">
          <h3 className="inv-dash-chart-title">🔧 Top 10 Used Spare Parts</h3>
          {topParts.length === 0 ? (
            <div className="inv-dash-empty">No usage data available</div>
          ) : (
            <div className="inv-dash-top-parts">
              {topParts.slice(0, 10).map((p, i) => (
                <div className="inv-dash-top-part-row" key={p.partId}>
                  <div className="inv-dash-top-rank">{i + 1}</div>
                  <div className="inv-dash-top-info">
                    <div className="inv-dash-top-name">{p.partName}</div>
                    <div className="inv-dash-top-number">{p.partNumber} • {p.vendor || '—'}</div>
                  </div>
                  <div className="inv-dash-top-qty">{fmt(p.totalQuantity)} units</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryDashboard;
